import { useNavigate, useLocation } from 'react-router-dom';
import { Building, Users, Tractor, Palmtree } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';

export default function MainLayout({ user, onLogout, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions(user);

  // Contar módulos de Producción accesibles
  const countProduccionModules = () => {
    if (isAdmin) return 99; // Admin tiene acceso a todo
    
    const produccionPermissions = [
      'agricultura.produccion.hoja_pesador.view',
      'agricultura.produccion.lotes.view',
      'agricultura.produccion.registros.view',
      'agricultura.produccion.reportes.view',
      'agricultura.produccion.salud_seguridad.view',
      'agricultura.produccion.cronograma.view',
      'agricultura.admin.tareas.view',
      'agricultura.produccion.mantenimiento.view',
      'agricultura.produccion.planilla.view',
    ];
    
    return produccionPermissions.filter(p => hasPermission(p)).length;
  };

  // Función para obtener la primera ruta permitida dentro de Producción
  const getFirstProduccionRoute = () => {
    if (isAdmin) return '/produccion';
    
    // Orden de prioridad: primero las más usadas
    const produccionRoutes = [
      { path: '/produccion/hoja-pesador', permission: 'agricultura.produccion.hoja_pesador.view' },
      { path: '/produccion/registro-labores', permission: 'agricultura.produccion.registros.view' },
      { path: '/produccion', permission: 'agricultura.produccion.lotes.view' },
      { path: '/produccion/reportes', permission: 'agricultura.produccion.reportes.view' },
      { path: '/produccion/salud-seguridad', permission: 'agricultura.produccion.salud_seguridad.view' },
      { path: '/produccion/cronograma', permission: 'agricultura.produccion.cronograma.view' },
      { path: '/produccion/tablero-tareas', permission: 'agricultura.admin.tareas.view' },
      { path: '/produccion/mantenimiento/picudos', permission: 'agricultura.produccion.mantenimiento.view' },
    ];
    
    const firstAllowedRoute = produccionRoutes.find(route => hasPermission(route.permission));
    return firstAllowedRoute ? firstAllowedRoute.path : '/produccion';
  };

  // Función para obtener la primera ruta permitida dentro de RRHH
  const getFirstRRHHRoute = () => {
    if (isAdmin) return '/rrhh';
    
    const rrhhRoutes = [
      { path: '/rrhh', permission: 'agricultura.rrhh.trabajadores.view' },
      { path: '/rrhh/asistencia', permission: 'agricultura.rrhh.asistencia.view' },
      { path: '/rrhh/disciplinario', permission: 'agricultura.rrhh.disciplinario.view' },
    ];
    
    const firstAllowedRoute = rrhhRoutes.find(route => hasPermission(route.permission));
    return firstAllowedRoute ? firstAllowedRoute.path : '/rrhh';
  };

  // Función para obtener la primera ruta permitida dentro de Maquinaria
  const getFirstMaquinariaRoute = () => {
    if (isAdmin) return '/maquinaria';
    
    const maquinariaRoutes = [
      { path: '/maquinaria', permission: 'agricultura.maquinaria.inventario.view' },
      { path: '/maquinaria/inspecciones', permission: 'agricultura.maquinaria.inspecciones.view' },
      { path: '/maquinaria/uso', permission: 'agricultura.maquinaria.uso.view' },
    ];
    
    const firstAllowedRoute = maquinariaRoutes.find(route => hasPermission(route.permission));
    return firstAllowedRoute ? firstAllowedRoute.path : '/maquinaria';
  };

  // Función para manejar el click en un módulo
  const handleModuleClick = (modulePath) => {
    if (modulePath === '/produccion') {
      navigate(getFirstProduccionRoute());
    } else if (modulePath === '/rrhh') {
      navigate(getFirstRRHHRoute());
    } else if (modulePath === '/maquinaria') {
      navigate(getFirstMaquinariaRoute());
    } else {
      navigate(modulePath);
    }
  };

  const isActiveModule = (path) => {
    return location.pathname.startsWith(path);
  };

  const allModules = [
    { 
      name: 'Administrativo', 
      path: '/administrativo', 
      icon: Building,
      check: () => isAdmin || hasAnyPermission([
        'agricultura.admin.empresas.view',
        'agricultura.admin.fincas.view',
        'agricultura.admin.usuarios.view',
        'agricultura.admin.historial.view',
        'agricultura.admin.tareas.view',
        'agricultura.admin.notificaciones.view'
      ])
    },
    { 
      name: 'R.R.H.H.', 
      path: '/rrhh', 
      icon: Users,
      check: () => isAdmin || hasAnyPermission([
        'agricultura.rrhh.trabajadores.view',
        'agricultura.rrhh.expediente.view',
        'agricultura.rrhh.asistencia.view',
        'agricultura.rrhh.disciplinario.view',
        'agricultura.rrhh.dashboard.view'
      ])
    },
    { 
      name: 'Producción', 
      path: '/produccion', 
      icon: Palmtree,
      check: () => isAdmin || hasAnyPermission([
        'agricultura.produccion.hoja_pesador.view',
        'agricultura.produccion.lotes.view',
        'agricultura.produccion.registros.view',
        'agricultura.produccion.reportes.view',
        'agricultura.produccion.salud_seguridad.view',
        'agricultura.produccion.cronograma.view',
        'agricultura.produccion.dashboard.view',
        'agricultura.produccion.mantenimiento.view'
      ])
    },
    { 
      name: 'Maquinaria', 
      path: '/maquinaria', 
      icon: Tractor,
      check: () => isAdmin || hasAnyPermission([
        'agricultura.maquinaria.inventario.view',
        'agricultura.maquinaria.inspecciones.view',
        'agricultura.maquinaria.uso.view',
        'agricultura.maquinaria.mantenimiento.view'
      ])
    },
  ];

  // Filtrar módulos según permisos
  const modules = allModules.filter(module => module.check());

  // Verificar si el usuario tiene vista simplificada (solo acceso a 1 módulo dentro de Producción)
  const produccionModulesCount = countProduccionModules();
  const hasOnlyOneProduccionModule = !isAdmin && produccionModulesCount === 1 && modules.length === 1 && modules[0].path === '/produccion';

  // Si solo tiene acceso a un módulo, no mostrar la barra de navegación principal
  if (hasOnlyOneProduccionModule) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <main className="max-w-7xl mx-auto">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main Module Tabs - Responsive con scroll horizontal automático */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mobile-tab-scroll -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="flex gap-0.5 sm:gap-1 min-w-max">
              {modules.map((module) => {
                const isActive = isActiveModule(module.path);
                const IconComponent = module.icon;
                return (
                  <button
                    key={module.path}
                    onClick={() => handleModuleClick(module.path)}
                    className={`
                      flex items-center px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0
                      ${isActive
                        ? 'border-emerald-600 text-emerald-600'
                        : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                    data-testid={`main-tab-${module.path.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    <IconComponent className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                    {module.name}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="dark:text-white">
        {children}
      </div>
    </div>
  );
}
